import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api, { formatPaise, formatError } from '@/lib/api';

const EMPTY = { code: '', amount: '', max_uses: '1', expires_at: '' };

/** Admin tab: create coupon codes (wallet credit) and switch them on or off. */
export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => api.get('/api/admin/coupons').then(({ data }) => setCoupons(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy('create');
    try {
      await api.post('/api/admin/coupons', {
        code: form.code, amount_paise: Math.round(parseFloat(form.amount) * 100) || 0,
        max_uses: parseInt(form.max_uses, 10) || 0, expires_at: form.expires_at || null,
      });
      toast.success('Coupon created');
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Could not create the coupon');
    }
    setBusy('');
  };

  const toggle = async (c) => {
    setBusy(c.code);
    try {
      await api.post(`/api/admin/coupons/${encodeURIComponent(c.code)}/active`, { active: !c.active });
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Could not update the coupon');
    }
    setBusy('');
  };

  const status = (c) => {
    if (!c.active) return ['Off', 'bg-zinc-500/10 text-muted-foreground border-zinc-500/30'];
    if (c.expires_at && new Date(c.expires_at) <= new Date()) return ['Expired', 'bg-zinc-500/10 text-muted-foreground border-zinc-500/30'];
    if (c.uses >= c.max_uses) return ['Used up', 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30'];
    return ['Active', 'bg-money-soft text-money border-money/40'];
  };

  return (
    <div className="space-y-4" data-testid="admin-coupons">
      <div className="p-4 rounded-xl bg-card border-2 border-ink space-y-3">
        <p className="text-sm font-semibold">New coupon</p>
        <p className="text-xs text-muted-foreground">Adds money to the customer's wallet. It pays for ads but can't be refunded to a bank, and you pay Meta for whatever it's spent on.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><Label className="text-xs mb-1 block">Code</Label>
            <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="DIWALI500" data-testid="coupon-code-input" /></div>
          <div><Label className="text-xs mb-1 block">Amount (₹)</Label>
            <Input type="number" min="1" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="500" data-testid="coupon-amount-input" /></div>
          <div><Label className="text-xs mb-1 block">Total uses</Label>
            <Input type="number" min="1" value={form.max_uses} onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))} data-testid="coupon-uses-input" /></div>
          <div><Label className="text-xs mb-1 block">Expires (optional)</Label>
            <Input type="date" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} data-testid="coupon-expiry-input" /></div>
        </div>
        <Button size="sm" className="gradient-bg" onClick={create}
          disabled={busy === 'create' || !form.code.trim() || !form.amount} data-testid="coupon-create-btn">
          {busy === 'create' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />} Create coupon
        </Button>
      </div>

      {coupons.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">No coupons yet.</p> : (
        <div className="space-y-2">
          {coupons.map(c => {
            const [label, color] = status(c);
            return (
              <div key={c.code} className="p-4 rounded-xl bg-card border-2 border-ink flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold font-mono">{c.code} <Badge variant="outline" className={`ml-1 text-[10px] ${color}`}>{label}</Badge></p>
                  <p className="text-xs text-muted-foreground">
                    {formatPaise(c.amount_paise)} · used {c.uses} of {c.max_uses}
                    {c.expires_at ? ` · expires ${new Date(c.expires_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}` : ' · no expiry'}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => toggle(c)} disabled={busy === c.code}>
                  {busy === c.code ? <Loader2 className="w-3 h-3 animate-spin" /> : c.active ? 'Switch off' : 'Switch on'}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
